/*
 * Product media gallery enhancements. Two jobs, both things the CSS can't do alone:
 *
 * 1. Mobile swipe sync. Below 990px, section-product-info.css turns the viewer <ul> from an
 *    absolutely-stacked one-slide-at-a-time list into a native scroll-snap track, so the main image
 *    can be swiped. Thumb -> image already works (media-gallery.js's setActiveMedia ends with
 *    `activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft })`). Handled here: aligning
 *    the track on load, and swipe -> active slide + active thumb.
 *
 * 2. Whole-thumb fit. The thumb strip is a fluid-width scroller, so any fixed thumb width leaves a
 *    sliced thumb at the edge and makes arrow paging land mid-thumb. This computes how many whole
 *    thumbs fit the measured track and writes the exact width to --pi-thumb-size, so the row is
 *    always an integer number of thumbs at every breakpoint and column ratio.
 *
 * Neither list node is ever replaced — product-info.js moves <li> children around on variant change
 * but keeps the lists — so listeners bound here survive variant switches.
 */
(() => {
  const MOBILE = '(max-width: 989px)';
  const SETTLE_MS = 120;
  // Figma thumb width (86 x 90). Acts as the cap: thumbs shrink to fit, never grow past the design.
  const THUMB_MAX = 86;

  class PiGallery {
    constructor(gallery) {
      this.gallery = gallery;
      this.viewer = gallery.querySelector('[id^="GalleryViewer"]');
      this.track = this.viewer && this.viewer.querySelector('ul');
      this.thumbs = gallery.querySelector('[id^="GalleryThumbnails"]');
      this.thumbTrack = this.thumbs && this.thumbs.querySelector('[id^="Slider-Thumbnails"]');

      this.mql = window.matchMedia(MOBILE);
      this.onSettle = this.onSettle.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.hasScrollEnd = 'onscrollend' in window;
      this.bound = false;

      if (this.thumbTrack) {
        this.initThumbFit();
        this.ensureActiveThumb();
        this.promoteActiveToFront();
        this.watchActiveThumb();
      }
      if (!this.track) return;

      this.mql.addEventListener('change', () => this.syncViewer());
      this.syncViewer();
    }

    /* ---------- thumb strip: whole thumbs only ---------- */

    initThumbFit() {
      this.fitThumbs();
      // the track's own width drives the thumb width, and thumb widths don't feed back into it
      // (flex: 1 1 auto + max-width), so observing the track is safe
      new ResizeObserver(() => this.fitThumbs()).observe(this.thumbTrack);
    }

    // The selected thumbnail is marked with aria-current, which the markup sets on first paint and
    // media-gallery.js moves on every media change. Both can leave the strip with nothing marked:
    // the markup when the variant's featured media is not one of the rendered thumbs, and
    // media-gallery.js when a media change finds no thumbnail to move the mark to. Either way the
    // shopper is looking at a strip where no thumb reads as the one on screen, so the mark is put
    // back here — on the thumb for whichever slide the viewer is actually showing, else the first.
    ensureActiveThumb() {
      const marked = Array.from(this.thumbTrack.querySelectorAll('button[aria-current]'));

      // The slide on screen is the authority — media-gallery.js sets it in the same breath as the
      // thumbnail mark, so agreeing with it never fights that. Failing that, whatever is already
      // marked stands, and failing that the first thumb, which is the one the viewer opens on.
      const activeSlide = this.track && this.track.querySelector('li.is-active');
      const mediaId = activeSlide && activeSlide.dataset.mediaId;
      const thumb =
        (mediaId && this.thumbTrack.querySelector(`[data-target="${mediaId}"]`)) ||
        (marked[0] && marked[0].closest('[data-target]')) ||
        this.thumbTrack.querySelector('[data-target]');

      const button = thumb && thumb.querySelector('button');
      if (!button) return;
      if (marked.length === 1 && marked[0] === button) return;

      marked.forEach((element) => {
        if (element !== button) element.removeAttribute('aria-current');
      });
      button.setAttribute('aria-current', 'true');
    }

    // The page opens on the selected variant's featured media, which sits wherever it happens to
    // sit in the product's media order — so on a fresh load the marked thumbnail could be third,
    // fifth, or scrolled out of the strip entirely, which reads as no thumbnail being marked at
    // all. A variant change does not have this problem: product-info.js calls setActiveMedia with
    // prepend, and media-gallery.js moves the active slide and its thumbnail to the front. This
    // does the same once on load, so the first paint agrees with every state after it.
    promoteActiveToFront() {
      const button = this.thumbTrack.querySelector('button[aria-current]');
      const thumb = button && button.closest('[data-target]');
      if (!thumb || thumb === this.thumbTrack.firstElementChild) return;

      const slide = this.track && this.track.querySelector(`li[data-media-id="${thumb.dataset.target}"]`);

      // Both lists move together or neither does — media-gallery.js pairs a thumbnail to a slide by
      // data-target / data-media-id, and the viewer opens on whichever slide is first.
      if (slide) this.track.prepend(slide);
      this.thumbTrack.prepend(thumb);
      this.thumbTrack.scrollTo({ left: 0 });
    }

    // Re-checked after a frame as well as at construction. media-gallery.js binds its own thumbnail
    // handling when the custom element upgrades, which can land either side of this script, and a
    // variant change re-renders the strip from the section; both can leave the first paint with
    // nothing marked, and the shopper sees that state, not the one the markup intended.
    watchActiveThumb() {
      requestAnimationFrame(() => this.ensureActiveThumb());

      new MutationObserver(() => this.ensureActiveThumb()).observe(this.thumbTrack, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['aria-current'],
      });
    }

    fitThumbs() {
      const width = this.thumbTrack.clientWidth;
      if (!width) return;

      const styles = getComputedStyle(this.thumbTrack);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      const items = this.thumbTrack.querySelectorAll('[data-target]');
      if (!items.length) return;

      // ceil, not floor: round up so the thumbs shrink to fill the row exactly instead of leaving
      // room for part of one more
      const perView = Math.max(1, Math.ceil((width + gap) / (THUMB_MAX + gap)));
      // 2dp — a rounded-up fraction would overflow the track and re-introduce the clipped thumb
      const size = Math.floor(((width - (perView - 1) * gap) / perView) * 100) / 100;

      if (size !== this.thumbSize) {
        this.thumbSize = size;
        this.thumbTrack.style.setProperty('--pi-thumb-size', `${size}px`);
      }

      this.thumbTrack.classList.toggle('pi-gallery__thumbs-list--fit', items.length <= perView);

      // one arrow tap = one full page of thumbs, instead of the markup's static step of 3
      this.thumbs.querySelectorAll('button[name="previous"], button[name="next"]').forEach((button) => {
        button.dataset.step = String(perView);
      });
    }

    /* ---------- main viewer: mobile swipe ---------- */

    get slides() {
      return Array.from(this.track.querySelectorAll('li[data-media-id]'));
    }

    syncViewer() {
      if (this.mql.matches) {
        this.bind();
        // wait for the flex/scroll-snap layout to apply before measuring offsets
        requestAnimationFrame(() => this.alignToActive());
      } else {
        this.unbind();
      }
    }

    bind() {
      if (this.bound) return;
      this.bound = true;
      if (this.hasScrollEnd) {
        this.track.addEventListener('scrollend', this.onSettle);
      } else {
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
      }
    }

    unbind() {
      if (!this.bound) return;
      this.bound = false;
      this.track.removeEventListener('scrollend', this.onSettle);
      this.track.removeEventListener('scroll', this.onScroll);
      clearTimeout(this.settleTimer);
    }

    // Safari < 18 has no scrollend — fall back to a short idle timer
    onScroll() {
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(this.onSettle, SETTLE_MS);
    }

    // Scroll position of a slide inside the track. Measured from rects rather than offsetLeft so it
    // stays correct whatever the offsetParent turns out to be — offsetLeft is relative to the
    // nearest *positioned* ancestor, which is the page for an unpositioned track, not the track.
    slideOffset(slide, trackLeft) {
      return slide.getBoundingClientRect().left - trackLeft + this.track.scrollLeft;
    }

    alignToActive() {
      const active = this.track.querySelector('li.is-active') || this.slides[0];
      if (!active) return;
      // jump, don't animate: CSS sets scroll-behavior: smooth on the track for thumb clicks
      const previous = this.track.style.scrollBehavior;
      this.track.style.scrollBehavior = 'auto';
      this.track.scrollLeft = this.slideOffset(active, this.track.getBoundingClientRect().left);
      this.track.style.scrollBehavior = previous;
    }

    onSettle() {
      const slides = this.slides;
      if (slides.length < 2) return;

      const trackLeft = this.track.getBoundingClientRect().left;
      const center = this.track.scrollLeft + this.track.clientWidth / 2;
      let landed = slides[0];
      let shortest = Infinity;

      slides.forEach((slide) => {
        const distance = Math.abs(this.slideOffset(slide, trackLeft) + slide.offsetWidth / 2 - center);
        if (distance < shortest) {
          shortest = distance;
          landed = slide;
        }
      });

      if (landed.classList.contains('is-active')) return;

      slides.forEach((slide) => slide.classList.remove('is-active'));
      landed.classList.add('is-active');
      this.highlightThumb(landed);
      if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
    }

    highlightThumb(slide) {
      const thumb = this.thumbs && this.thumbs.querySelector(`[data-target="${slide.dataset.mediaId}"]`);
      if (!thumb) return;

      // media-gallery.js debounces its own slideChanged handler by 500ms, which reads as lag on a
      // swipe — call the element's method directly and fall back to the event if it isn't there.
      if (typeof this.gallery.setActiveThumbnail === 'function') {
        this.gallery.setActiveThumbnail(thumb);
      } else {
        this.viewer.dispatchEvent(new CustomEvent('slideChanged', { detail: { currentElement: slide } }));
      }
    }
  }

  const init = (root = document) => {
    root.querySelectorAll('media-gallery.pi-gallery').forEach((gallery) => {
      if (gallery.piGallery) return;
      gallery.piGallery = new PiGallery(gallery);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
